import clsx from 'clsx';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  BODY_MAP_REGIONS,
  BODY_MAP_SIDES,
  bodyMapHotspotsForView,
  bodyMapRegionHasView,
  isMidlineBodyMapRegion,
  type BodyMapHotspot,
  type BodyMapRegion,
  type BodyMapSide,
  type BodyMapView,
} from '@emr/shared';

/**
 * The click-to-place body-map diagram (`BACKLOG.md` M10a), replacing a plain region `<select>` plus
 * a plain side `<select>` with a single anterior/posterior figure a clinician can click a hotspot on.
 *
 * **This component owns no coordinate logic of its own.** Every position comes from
 * `@emr/shared`'s `bodyMapGeometry.ts`, specifically so the laterality transform is the one place it
 * can be gotten right or wrong — `docs/comparison-emrx.md` §6.2's bug (a posterior-view right-sided
 * point rendered on the wrong half) is a coordinate-layer bug, not a rendering one, and stays fixed
 * for every caller of this component rather than needing a per-screen check.
 *
 * **The underlying `<select>` semantics stay available**, per `claude-kit/rules/invariants.md`'s
 * in-app-help section and this task's own accessibility requirement: two real, fully functional
 * `<select>` elements (region and side) are always rendered, kept in sync with the same controlled
 * `region`/`side` props the SVG hotspots write to — visually hidden by default, but not
 * `display:none`, so a keyboard user reaches them by Tab and a screen-reader user gets the ordinary
 * select experience with all options every time, not gated behind first finding the SVG. The visible
 * hotspots are additive, not a replacement — the wrapping `<label>` for each hidden select uses the
 * same `sr-only focus-within:not-sr-only` pattern `AppShell.tsx`'s own skip-link uses on itself, for
 * the same reason (reachable by keyboard, invisible until that's how it's being used).
 */

export interface BodyMapPickerProps {
  readonly region: BodyMapRegion;
  readonly side: BodyMapSide | null;
  readonly onChange: (next: { region: BodyMapRegion; side: BodyMapSide | null }) => void;
  /** The group's own visible label — the caller's existing field label (e.g. "Site — region"),
   * reused rather than duplicated so the two selects this replaces keep their exact translated
   * copy. */
  readonly groupLabel: string;
  readonly regionLabel: (region: BodyMapRegion) => string;
  readonly sideLabel: (side: BodyMapSide) => string;
  /** Label for "no side recorded" — the same copy the replaced side `<select>`'s own N/A option
   * used, so the hidden select reads identically to before. */
  readonly notApplicableLabel: string;
}

function svgY(rawY: number): number {
  // The geometry table's own viewBox is 0–100 x, 0–200 y (`bodyMapGeometry.ts`'s header). Percentage
  // positioning against the picker's own aspect-locked container needs y as a 0–100 percentage too.
  return (rawY / 200) * 100;
}

/** A simplified, non-anatomical human outline — decoration only (`aria-hidden`), not a source of
 * hotspot geometry. `view` only changes a small decorative mark (a "spine" line on the back, none on
 * the front) so the two views are visually distinguishable without claiming anatomical precision. */
function FigureOutline({ view }: { readonly view: BodyMapView }) {
  return (
    <svg viewBox="0 0 100 200" aria-hidden="true" className="absolute inset-0 h-full w-full">
      <g className="fill-surface stroke-line-strong" strokeWidth={1.5}>
        <ellipse cx={50} cy={15} rx={11} ry={13} />
        <rect x={45} y={26} width={10} height={8} />
        <rect x={30} y={31} width={40} height={70} rx={14} />
        <rect x={14} y={36} width={13} height={62} rx={6} />
        <rect x={73} y={36} width={13} height={62} rx={6} />
        <ellipse cx={20.5} cy={101} rx={7} ry={6} />
        <ellipse cx={79.5} cy={101} rx={7} ry={6} />
        <rect x={32} y={100} width={15} height={85} rx={7} />
        <rect x={53} y={100} width={15} height={85} rx={7} />
        <ellipse cx={39.5} cy={190} rx={8} ry={5} />
        <ellipse cx={60.5} cy={190} rx={8} ry={5} />
      </g>
      {view === 'posterior' ? (
        <line
          x1={50}
          y1={32}
          x2={50}
          y2={98}
          className="stroke-line-strong"
          strokeWidth={1}
          strokeDasharray="3 3"
        />
      ) : null}
    </svg>
  );
}

function hotspotIsSelected(
  hotspot: BodyMapHotspot,
  region: BodyMapRegion,
  side: BodyMapSide | null,
): boolean {
  if (hotspot.region !== region) return false;
  if (hotspot.side === null) return true; // midline hotspot: region match is the whole story
  return hotspot.side === side;
}

export function BodyMapPicker({
  region,
  side,
  onChange,
  groupLabel,
  regionLabel,
  sideLabel,
  notApplicableLabel,
}: BodyMapPickerProps) {
  const { t } = useTranslation('bodyMapPicker');
  const regionSelectId = useId();
  const sideSelectId = useId();
  const [requestedView, setRequestedView] = useState<BodyMapView>(
    bodyMapRegionHasView(region, 'anterior') ? 'anterior' : 'posterior',
  );

  // Derived at render time, not synchronised via an effect: if the caller's own `region` changes to
  // one `requestedView` doesn't show (e.g. the hidden select is used directly to jump to `back`),
  // render whichever view does show it instead, rather than leaving the figure on a view with no
  // selected hotspot visible at all. `requestedView` itself is left untouched, so the user's last
  // explicit toggle choice is what the figure returns to once `region` is compatible with it again.
  const view: BodyMapView = bodyMapRegionHasView(region, requestedView)
    ? requestedView
    : bodyMapRegionHasView(region, 'anterior')
      ? 'anterior'
      : 'posterior';

  const midline = isMidlineBodyMapRegion(region);
  const hotspots = bodyMapHotspotsForView(view);

  const selectedLabel = midline
    ? regionLabel(region)
    : side === null || side === 'midline'
      ? regionLabel(region)
      : `${sideLabel(side)} ${regionLabel(region)}`;

  return (
    <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
      <legend className="p-0 text-sm font-medium text-ink">{groupLabel}</legend>

      <div
        role="group"
        aria-label={t('viewToggleLabel')}
        className="inline-flex w-fit gap-1 rounded-control border border-line p-0.5"
      >
        {(['anterior', 'posterior'] as const).map((candidate) => (
          <button
            key={candidate}
            type="button"
            aria-pressed={view === candidate}
            onClick={() => {
              setRequestedView(candidate);
            }}
            className={clsx(
              'rounded-control px-2.5 py-1 text-sm font-medium transition-colors',
              view === candidate ? 'bg-accent text-accent-ink' : 'text-ink-muted hover:text-ink',
            )}
          >
            {candidate === 'anterior' ? t('viewAnterior') : t('viewPosterior')}
          </button>
        ))}
      </div>

      <div className="relative mx-auto aspect-[100/200] w-full max-w-[220px]">
        <FigureOutline view={view} />
        {hotspots.map((hotspot) => {
          const selected = hotspotIsSelected(hotspot, region, side);
          const hotspotDescription =
            hotspot.side === null
              ? regionLabel(hotspot.region)
              : `${sideLabel(hotspot.side)} ${regionLabel(hotspot.region)}`;
          return (
            <button
              key={`${hotspot.region}-${hotspot.side ?? 'mid'}`}
              type="button"
              aria-pressed={selected}
              aria-label={t('hotspotAriaLabel', { site: hotspotDescription })}
              title={hotspotDescription}
              onClick={() => {
                onChange({ region: hotspot.region, side: hotspot.side });
              }}
              style={{ left: `${hotspot.point.x}%`, top: `${svgY(hotspot.point.y)}%` }}
              className={clsx(
                'absolute -translate-x-1/2 -translate-y-1/2 rounded-full border-2 transition-all',
                selected
                  ? 'size-4 border-accent-text bg-accent'
                  : 'size-2.5 border-field-line bg-field hover:border-accent-text',
              )}
            />
          );
        })}
      </div>

      <p className="m-0 text-sm text-ink-muted">{t('selectedSummary', { site: selectedLabel })}</p>

      {/* The real, fully functional selects — see this file's own header. Visually hidden until
          focused, matching `AppShell.tsx`'s skip-link. */}
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        <label
          htmlFor={regionSelectId}
          className="sr-only focus-within:not-sr-only flex flex-col gap-1.5"
        >
          <span className="text-sm font-medium text-ink">{groupLabel}</span>
          <select
            id={regionSelectId}
            value={region}
            onChange={(event) => {
              const nextRegion = event.target.value as BodyMapRegion;
              onChange({
                region: nextRegion,
                side: isMidlineBodyMapRegion(nextRegion) ? null : side,
              });
            }}
            className="rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
          >
            {BODY_MAP_REGIONS.map((value) => (
              <option key={value} value={value}>
                {regionLabel(value)}
              </option>
            ))}
          </select>
        </label>
        <label
          htmlFor={sideSelectId}
          className="sr-only focus-within:not-sr-only flex flex-col gap-1.5"
        >
          <span className="text-sm font-medium text-ink">{t('sideFieldLabel')}</span>
          <select
            id={sideSelectId}
            value={side ?? ''}
            disabled={midline}
            onChange={(event) => {
              const raw = event.target.value;
              onChange({ region, side: raw === '' ? null : (raw as BodyMapSide) });
            }}
            className="rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
          >
            <option value="">{notApplicableLabel}</option>
            {BODY_MAP_SIDES.map((value) => (
              <option key={value} value={value}>
                {sideLabel(value)}
              </option>
            ))}
          </select>
        </label>
      </div>
    </fieldset>
  );
}
